/**
 * /music-stand — the library on this device (IndexedDB).
 *
 * The point of a stand is that the music is still there at the next
 * rehearsal, so the files themselves are kept (a PDF as it came, photos
 * downscaled once on import), not just their names. Stores:
 *   pieces    {id, name, kind:'pdf'|'images', pages, files, bytes, created,
 *              opened, lastPage, links:[…], marks:[…], thumb (Blob)}
 *   files     {piece, idx, blob, name}           key [piece, idx]
 *   analysis  {piece, page, box, prof, pw, ph, split, splitUser}  key [piece, page]
 *   ink       {piece, page, items:[…]}           key [piece, page]
 *   setlists  {id, name, items:[pieceId…], created}
 *
 * When IndexedDB is not there at all (some private windows), the same API
 * runs from memory and says so — the page tells the musician the library
 * will not survive closing the tab, instead of pretending.
 */

const DB_NAME = 'timbrica_music_stand';
const VERSION = 1;

function req(r) {
    return new Promise((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });
}

function memStore() {
    const t = { pieces: new Map(), files: new Map(), analysis: new Map(), ink: new Map(), setlists: new Map() };
    const k = (v) => (Array.isArray(v) ? v.join('|') : String(v));
    const keyOf = (name, v) => (name === 'pieces' || name === 'setlists' ? v.id : [v.piece, name === 'files' ? v.idx : v.page]);
    return {
        persistent: false,
        async get(name, key) { return t[name].get(k(key)) || null; },
        async put(name, val) { t[name].set(k(keyOf(name, val)), val); },
        async del(name, key) { t[name].delete(k(key)); },
        async all(name) { return Array.from(t[name].values()); },
        async byPiece(name, id) { return Array.from(t[name].values()).filter((v) => v.piece === id); },
        async addPiece(piece, files) {
            t.pieces.set(k(piece.id), piece);
            files.forEach((f, idx) => t.files.set(k([piece.id, idx]), { piece: piece.id, idx, blob: f.blob, name: f.name || '' }));
        },
        async delPiece(id) {
            for (const n of ['files', 'analysis', 'ink']) for (const [key, v] of t[n]) if (v.piece === id) t[n].delete(key);
            t.pieces.delete(String(id));
        },
    };
}

export async function openStore() {
    if (typeof indexedDB === 'undefined') return memStore();
    let db;
    try {
        const r = indexedDB.open(DB_NAME, VERSION);
        r.onupgradeneeded = () => {
            const d = r.result;
            if (!d.objectStoreNames.contains('pieces')) d.createObjectStore('pieces', { keyPath: 'id' });
            if (!d.objectStoreNames.contains('files')) d.createObjectStore('files', { keyPath: ['piece', 'idx'] }).createIndex('piece', 'piece');
            if (!d.objectStoreNames.contains('analysis')) d.createObjectStore('analysis', { keyPath: ['piece', 'page'] }).createIndex('piece', 'piece');
            if (!d.objectStoreNames.contains('ink')) d.createObjectStore('ink', { keyPath: ['piece', 'page'] }).createIndex('piece', 'piece');
            if (!d.objectStoreNames.contains('setlists')) d.createObjectStore('setlists', { keyPath: 'id' });
        };
        db = await Promise.race([
            req(r),
            new Promise((_, rej) => setTimeout(() => rej(new Error('idb_open_timeout')), 8000)),
        ]);
    } catch (e) {
        const m = memStore();
        m.reason = String((e && (e.name || e.message)) || e).slice(0, 60);
        return m;
    }
    db.onversionchange = () => { try { db.close(); } catch (_) { /* another tab upgrades */ } };

    const tx = (name, mode = 'readonly') => db.transaction(name, mode).objectStore(name);
    const done = (t) => new Promise((res, rej) => { t.oncomplete = () => res(); t.onerror = () => rej(t.error); t.onabort = () => rej(t.error || new Error('abort')); });

    return {
        persistent: true,
        async get(name, key) { return (await req(tx(name).get(key))) || null; },
        async put(name, val) {
            const t = db.transaction(name, 'readwrite');
            t.objectStore(name).put(val);
            await done(t);
        },
        async del(name, key) {
            const t = db.transaction(name, 'readwrite');
            t.objectStore(name).delete(key);
            await done(t);
        },
        async all(name) { return req(tx(name).getAll()); },
        async byPiece(name, id) { return req(tx(name).index('piece').getAll(id)); },
        /** Put a piece with all its files in ONE transaction — a half-saved piece is worse than none. */
        async addPiece(piece, files) {
            const t = db.transaction(['pieces', 'files'], 'readwrite');
            t.objectStore('pieces').put(piece);
            const fs = t.objectStore('files');
            files.forEach((f, idx) => fs.put({ piece: piece.id, idx, blob: f.blob, name: f.name || '' }));
            await done(t);
        },
        async delPiece(id) {
            const t = db.transaction(['pieces', 'files', 'analysis', 'ink'], 'readwrite');
            t.objectStore('pieces').delete(id);
            for (const n of ['files', 'analysis', 'ink']) {
                const idx = t.objectStore(n).index('piece');
                const r = idx.openKeyCursor(IDBKeyRange.only(id));
                r.onsuccess = () => { const c = r.result; if (c) { t.objectStore(n).delete(c.primaryKey); c.continue(); } };
            }
            await done(t);
        },
    };
}

/** Ask the browser to keep the library when the device runs low on space. */
export async function askPersist() {
    try {
        if (navigator.storage && navigator.storage.persisted && await navigator.storage.persisted()) return true;
        if (navigator.storage && navigator.storage.persist) return await navigator.storage.persist();
    } catch (_) { /* not offered here */ }
    return false;
}

export function newId() {
    const a = new Uint8Array(8);
    (globalThis.crypto || window.crypto).getRandomValues(a);
    return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
}
